import { legacyBlogTarget } from "@/lib/legacy-blog-paths";
import { legacyPageRedirect } from "@/lib/legacy-page-redirects";
import { legacyResultsRedirect } from "@/lib/legacy-results-slugs";
import { LEGACY_UPLOAD_REDIRECTS } from "@/lib/legacy-uploads";

// Blog post HTML in Supabase came from WordPress. Its internal links point at
// www.restorehair.com, old WordPress slugs, clone-of-* duplicates, /blog/...
// paths, double slashes and paths without the trailing slash. Each one costs
// the reader and the crawler one or two redirects. This rewrites each
// internal href and src to the final URL when the page renders. The stored
// HTML does not change.

// Also matches the "htps:" typo found in one post.
const SITE_HOST = /^(?:h?t*ps?:)?\/\/(?:www\.|lp\.)?restorehair\.com(?=[/?#]|$)/i;

// Paths that a dedicated route file redirects. The maps above do not know
// them. Keep each entry the same as its route file.
const ROUTE_REDIRECTS: Record<string, string> = {
  "/ever-hair/": "/non-surgical/", // ever-hair.tsx
  "/laser-therapy-for-hair-loss/": "/low-level-laser-therapy-for-hair-loss/", // laser-therapy-for-hair-loss.tsx
  "/will-i-go-bald-calculator/": "/", // will-i-go-bald-calculator.tsx
  "/hair-cloning-follicle-banking/": "/hair-loss-blog/", // hair-cloning-follicle-banking.tsx
  "/clone-of-beard-transplants/": "/beard-transplant/", // clone-of-beard-transplants.tsx
  "/faq/how-often-should-i-get-my-hair-cut/": "/hair-transplant-faqs/", // faq.$slug.tsx: no such question
  "/schedule-a-consult/lake-forest/": "/schedule-a-consult/oak-brook/", // schedule-a-consult.lake-forest.tsx
};

/** The final path for an internal path, without the query string. */
export function finalInternalPath(rawPath: string): string {
  // Slugs never contain spaces. Some imported hrefs do ("/ever-hair/ /").
  const cleaned = rawPath.replace(/(?:%20|\s)+/g, "");
  const path = `/${cleaned.replace(/^\/+/, "")}`.replace(/\/{2,}/g, "/");
  if (path === "/") return path;
  if (path.startsWith("/wp-content/")) {
    return LEGACY_UPLOAD_REDIRECTS[path.slice("/wp-content/".length)] ?? path;
  }
  const parts = path.split("/").filter(Boolean);
  const last = parts[parts.length - 1];
  // A file (image, PDF): keep it as it is.
  if (last.includes(".")) return path;
  const routeTarget = ROUTE_REDIRECTS[`/${parts.join("/")}/`];
  if (routeTarget) return routeTarget;
  if (parts[0] === "blog" && parts.length > 1) return legacyBlogTarget(parts.slice(1).join("/"));
  if (parts.length === 1) {
    const slug = parts[0];
    return legacyResultsRedirect(slug) ?? legacyPageRedirect(slug) ?? `/${slug}/`;
  }
  return `/${parts.join("/")}/`;
}

function rewriteUrl(url: string): string {
  let rest: string;
  if (SITE_HOST.test(url)) rest = url.replace(SITE_HOST, "");
  else if (url.startsWith("/") && !url.startsWith("//")) rest = url;
  else return url;
  const match = /^([^?#]*)(.*)$/.exec(rest);
  const pathPart = match?.[1] ?? "";
  const suffix = match?.[2] ?? "";
  return finalInternalPath(pathPart || "/") + suffix;
}

/** Point every internal href and src in post HTML at its final URL. */
export function rewriteInternalLinks(html: string): string {
  return html.replace(
    /\b(href|src)=("|')([^"']*)\2/gi,
    (_m, attr: string, quote: string, url: string) => `${attr}=${quote}${rewriteUrl(url)}${quote}`,
  );
}

/**
 * The post page already renders the post title as its one <h1>. Some
 * imported post bodies also contain an <h1>, so the page had two. Turn every
 * <h1> in the body into an <h2>.
 */
export function demoteHeadings(html: string): string {
  return html.replace(/<(\/?)h1(?=[\s>])/gi, "<$1h2");
}
